// Defaults for the break-even tracker.
//
// Fixed-cost amounts come from kinetic-kids-llc-profit-loss-20260620.xlsx,
// trailing twelve months Jul 2025 - Jun 2026, divided to a quarter. Franchise
// terms come from the Skyhawks 2023 FDD Review Sheet. Program drivers are
// derived live from the API and are not listed here.
//
// The $13,249 franchise fee in Sep 2025 is a one-time territory purchase and
// is deliberately excluded from every run-rate below.

export const SEASON_ORDER = ['winter', 'spring', 'summer', 'fall']

export const DEFAULT_COST_LINES = [
  { id: 'storage', name: 'Storage rental', amount: 2235, note: '$745/mo', enabled: true },
  { id: 'insurance', name: 'Insurance (liability + workers comp)', amount: 806, note: 'books TTM', enabled: true },
  { id: 'software', name: 'Software & apps', amount: 836, note: 'books TTM', enabled: true },
  { id: 'licenses', name: 'Business licenses & permits', amount: 250, note: 'books TTM', enabled: true },
  { id: 'accounting', name: 'Accounting', amount: 225, note: 'books TTM', enabled: true },
  { id: 'office', name: 'Office & supplies', amount: 141, note: 'books TTM', enabled: true },
  { id: 'general', name: 'General business & training', amount: 84, note: 'books TTM', enabled: true },
  { id: 'taxes', name: 'CA LLC fee & taxes', amount: 489, note: 'books TTM', enabled: true },
  { id: 'cardFees', name: 'Card transaction fees', amount: 125, note: 'books TTM', enabled: true },
  { id: 'techFee', name: 'Skyhawks technology fee', amount: 600, note: '$200/mo, FDD', enabled: true },
  { id: 'equipment', name: 'Equipment replacement', amount: 0, note: 'not restocked since 2024', enabled: false },
]

export const DEFAULT_SETTINGS = {
  // Coach wages modeled from program data ran 1.75x actuals for Mar-May 2026,
  // most likely because one coach covers concurrent or back-to-back classes and
  // some programs are taught by managers. Calibrate rather than assume.
  calibration: 0.57,
  coachRate: 21,
  coachBurden: 0.1425, // FICA 7.65 + FUTA/SUI 2.5 + ETT 0.1 + workers comp ~4
  managerBurden: 0.11, // clerical workers comp class code, not athletic instruction

  managerCount: 2,
  managerHoursPerWeek: 10,
  managerRate: 25,

  summerCampMix: 0.48,
  campShirtPerKid: 10, // shirt + merit award, contractually required for camps

  royaltyRate: 0.09,
  royaltyMinMonthly: 900, // the $1,100 paid monthly is $900 royalty + $200 tech fee
  yearlyReconciliation: false,
  royaltyOnGross: false,

  localAdRate: 0.03, // FDD requires >= 3% of gross revenue on local marketing
  adBudgetPerQuarter: 673,

  costLines: DEFAULT_COST_LINES,
}

export const STORAGE_KEY = 'skyhawks.breakEven.settings.v1'

export function loadSettings() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...DEFAULT_SETTINGS }
    const saved = JSON.parse(raw)
    // Merge cost lines by id so new default lines appear for existing users
    // without discarding amounts they have already edited.
    const savedById = new Map((saved.costLines || []).map((c) => [c.id, c]))
    const costLines = DEFAULT_COST_LINES.map((line) => ({
      ...line,
      ...(savedById.get(line.id) || {}),
    }))
    return { ...DEFAULT_SETTINGS, ...saved, costLines }
  } catch (e) {
    return { ...DEFAULT_SETTINGS }
  }
}

export function saveSettings(settings) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
  } catch (e) {
    // Storage can be unavailable in private browsing; settings simply
    // fall back to defaults next visit.
  }
}
